import { StepViewModel, WaterfallStepAction, FieldMetadata } from '../../models/waterfall';

/**
 * Generate a unique GUID for a step
 */
export const generateStepGuid = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Create a new empty step
 */
export const createEmptyStep = (isInitial = false): StepViewModel => {
  return {
    stepGuid: generateStepGuid(),
    description: isInitial ? 'Initial Step' : '',
    integrationSettingsId: 0,
    waterfallRules: null,
    nextSteps: null,
    stepTimeoutInSeconds: null,
    onTimeoutExceededAction: WaterfallStepAction.Continue,
    onFatalErrorAction: WaterfallStepAction.Continue,
  };
};

/**
 * Add a new step to a parent step's nextSteps array
 */
export const addStepToParent = (parentStep: StepViewModel, newStep: StepViewModel): void => {
  if (!parentStep.nextSteps) {
    parentStep.nextSteps = [newStep];
  } else {
    parentStep.nextSteps.push(newStep);
  }
};

/**
 * Recursively find and update a step in the tree
 */
export const findAndUpdateStep = (
  rootStep: StepViewModel,
  targetGuid: string,
  updater: (step: StepViewModel) => void
): boolean => {
  if (rootStep.stepGuid === targetGuid) {
    updater(rootStep);
    return true;
  }

  if (rootStep.nextSteps) {
    for (const nextStep of rootStep.nextSteps) {
      if (findAndUpdateStep(nextStep, targetGuid, updater)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Recursively find and delete a step from the tree
 */
export const findAndDeleteStep = (
  rootStep: StepViewModel,
  targetGuid: string,
  parentStep: StepViewModel | null = null
): boolean => {
  // If we're trying to delete the root step, we can't do it
  if (rootStep.stepGuid === targetGuid && !parentStep) {
    return false;
  }

  // Check if the target is in the current step's nextSteps
  if (rootStep.nextSteps) {
    const index = rootStep.nextSteps.findIndex((step) => step.stepGuid === targetGuid);
    if (index !== -1) {
      rootStep.nextSteps.splice(index, 1);
      // Clean up empty arrays
      if (rootStep.nextSteps.length === 0) {
        rootStep.nextSteps = null;
      }
      return true;
    }

    // Recursively search in child steps
    for (const nextStep of rootStep.nextSteps) {
      if (findAndDeleteStep(nextStep, targetGuid, rootStep)) {
        return true;
      }
    }
  }

  return false;
};

/**
 * Check if a step has minimum required fields
 */
export const isStepValid = (step: StepViewModel): boolean => {
  return !!(
    step.description && 
    step.description.trim() !== '' && 
    step.integrationSettingsId && 
    step.integrationSettingsId > 0
  );
};

/**
 * Recursively check if all steps in the tree are valid
 */
export const areAllStepsValid = (step: StepViewModel): boolean => {
  if (!isStepValid(step)) {
    return false;
  }

  if (step.nextSteps && step.nextSteps.length > 0) {
    return step.nextSteps.every(nextStep => areAllStepsValid(nextStep));
  }

  return true;
};

/**
 * Deep clone a step (to avoid mutation issues)
 */
export const cloneStep = (step: StepViewModel): StepViewModel => {
  return JSON.parse(JSON.stringify(step));
};

/**
 * Filter WaterfallContext fields based on vendor.
 * 
 * Fields can be:
 * 1. Generic fields (no "VendorOptions" in fieldName) - always included
 * 2. Vendor-specific fields (contains "VendorOptions.{VendorName}Settings") - only included for matching vendor
 * 
 * @param fields - The full list of field metadata from WaterfallContext
 * @param vendorName - The vendor name (e.g., "SmartFees", "Factual", "Xactus")
 * @returns Filtered list of fields that are either generic or match the vendor
 * 
 * @example
 * // Generic field: "UserId" -> always included
 * // Vendor field for SmartFees: "VendorOptions.SmartFeesSettings.SomeField" -> included if vendor is "SmartFees"
 * // Vendor field for Factual: "VendorOptions.FactualSettings.OtherField" -> excluded if vendor is "SmartFees"
 */
export const filterFieldsByVendor = (
  fields: FieldMetadata[],
  vendorName: string | null | undefined
): FieldMetadata[] => {
  if (!fields || fields.length === 0) {
    return [];
  }

  // If no vendor specified, return all generic fields only
  if (!vendorName) {
    return fields.filter((field) => {
      const fieldName = field.fieldName || '';
      return !fieldName.includes('VendorOptions');
    });
  }

  return fields.filter((field) => {
    const fieldName = field.fieldName || '';

    // Include generic fields (not vendor-specific)
    if (!fieldName.includes('VendorOptions')) {
      return true;
    }

    // Include fields for the selected vendor
    // Pattern: VendorOptions.{VendorName}Settings.*
    if (fieldName.includes(`VendorOptions.${vendorName}Settings`)) {
      return true;
    }

    // Filter out fields for other vendors
    return false;
  });
};
